import React, { FC, useEffect, useState } from 'react';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { ApolloError, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Loader from 'components/shared/loader/loader.component';
import { flagInvoiceAsPaidTemplate } from 'apollo/templates/invoices';
import { Invoice } from 'models/invoice';

type SetInvoiceAsPaidComponentProps = {
    open: boolean;
    invoiceId: string;
    invoiceSentDate: Date;
    onDialogClose: () => void;
    onPaymentDateSet: (error?: ApolloError, invoice?: Invoice) => void;
};

const SetInvoiceAsPaidComponent: FC<SetInvoiceAsPaidComponentProps> = ({
    open,
    invoiceId,
    invoiceSentDate,
    onDialogClose,
    onPaymentDateSet,
}: SetInvoiceAsPaidComponentProps) => {
    const { t: translate } = useTranslation(['common', 'invoices']);
    const [selectedPaymentDate, setSelectedPaymentDate] = useState<Date>(new Date());
    const [selectedSentDate, setSelectedSentDate] = useState<Date>(new Date());
    useEffect(() => {
        setSelectedSentDate(invoiceSentDate || new Date());
    }, [invoiceSentDate]);

    const [flagInvoiceAsPaid, { loading }] = useMutation(flagInvoiceAsPaidTemplate, {
        onCompleted: ({ flagInvoiceAsPaid: invoice }) => {
            onPaymentDateSet(undefined, invoice as Invoice);
        },
        onError: (error: ApolloError) => {
            onPaymentDateSet(error, undefined);
        },
    });

    const handleClick = async () => {
        await flagInvoiceAsPaid({ variables: { invoiceId, paidOn: selectedPaymentDate, sentDate: selectedSentDate } });
    };

    const handlePaymentDateChange = (date: Date | null) => {
        setSelectedPaymentDate(date as Date);
    };

    const handleSentDateChange = (date: Date | null) => {
        setSelectedSentDate(date as Date);
    };

    return (
        <Dialog maxWidth="md" fullWidth open={open} onClose={onDialogClose}>
            <DialogTitle>{translate('invoices:REGISTER_PAYMENT_DATE')}</DialogTitle>
            <DialogContent>
                <Box mt={1}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">{translate('invoices:SENT_DATE')}</Typography>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                                <div>
                                    <DatePicker
                                        format={getDatePickerFormat()}
                                        maxDate={new Date()}
                                        value={selectedSentDate}
                                        onChange={handleSentDateChange}
                                    />
                                </div>
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">{translate('invoices:PAYMENT_DATE')}</Typography>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                                <div>
                                    <DatePicker
                                        format={getDatePickerFormat()}
                                        maxDate={new Date()}
                                        value={selectedPaymentDate}
                                        onChange={handlePaymentDateChange}
                                    />
                                </div>
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            {loading ? (
                <Loader />
            ) : (
                <DialogActions>
                    <Button onClick={onDialogClose}>{translate('common:CANCEL')}</Button>
                    <Button
                        disabled={!selectedPaymentDate || !selectedSentDate}
                        onClick={handleClick}
                        variant="contained"
                        type="button"
                    >
                        {translate('invoices:SET_PAID')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default SetInvoiceAsPaidComponent;
